<template>
  <div v-if="show" class="modal-overlay">
    <div class="modal-content">
      <button class="close-button" @click="handleClose">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M4.16504 4.16602L15.8309 15.8319" stroke="#5A5C62" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M4.1642 15.8319L15.8301 4.16602" stroke="#5A5C62" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>

      <div class="modal-header">
        <h2 class="modal-header-title">{{ $t('jobNotifications.title') }}</h2>
        <p class="modal-header-subtitle">{{ $t('jobNotifications.subtitle') }}</p>
      </div>

      <div class="modal-body">
        <div class="filters-container">
          <!-- <div class="tenant-info">
            <span class="tenant-label">Selected Tenant:</span>
            <span class="tenant-value">{{ tenant.tenant_name }}</span>
          </div> -->
          <div class="filters-row">
            <div class="role-filter">
              <select v-model="selectedRole" @change="filterUsers" class="role-select">
                <option value="">{{ $t('jobNotifications.filters.all_roles') }}</option>
                <option value="Tenant User">{{ $t('jobNotifications.filters.tenant_user') }}</option>
                <option value="Tenant Admin">{{ $t('jobNotifications.filters.tenant_admin') }}</option>
              </select>
            </div>
          </div>
          <div class="search-container">
            <div class="search-bar">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                <circle cx="6.5" cy="7.45996" r="5.75" stroke="#8F969B" stroke-width="1.5" />
                <path d="M11 11.96L15 15.96" stroke="#8F969B" stroke-width="1.5" stroke-linecap="round" />
              </svg>
              <input type="text" class="search-input" :placeholder="$t('jobNotifications.filters.search_placeholder')" v-model="search" @input="filterUsers" />
            </div>
          </div>
        </div>

        <div class="users-list">
          <div v-if="loading" class="loading-container">
            <div class="loading-spinner"></div>
            <span class="loading-text">{{ $t('jobNotifications.loading') }}</span>
          </div>
          <template v-else>
            <div v-if="filteredUsers.length === 0" class="no-results">
              <span class="no-results-text">{{ $t('jobNotifications.no_users_found') }}</span>
            </div>
            <table v-else class="users-table">
              <thead>
                <tr>
                  <th class="srno-header">{{ $t('jobNotifications.table.sr_no') }}</th>
                  <th class="name-header">{{ $t('jobNotifications.table.name') }}</th>
                  <th class="role-header">{{ $t('jobNotifications.table.role') }}</th>
                  <th class="action-header">
                    <span class="select-all-label-text">{{ $t('jobNotifications.table.select_all') }}</span>
                    <input type="checkbox" :checked="areAllFilteredUsersSelected" @change="toggleSelectAll" class="select-all-checkbox" />
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(user, index) in filteredUsers" :key="user.id" class="user-row">
                  <td class="user-srno">{{ index + 1 }}</td>
                  <td class="user-name">{{ user.name }}</td>
                  <td class="user-role">
                    {{
                      user.user_roles
                        ?.map((role) => {
                          if (role.role.roleName === 'Tenant Admin') return $t('jobNotifications.roles.admin')
                          if (role.role.roleName === 'Tenant User') return $t('jobNotifications.roles.user')
                          return role.role.roleName
                        })
                        .join(', ') || ''
                    }}
                  </td>
                  <td class="user-action">
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="user.notification_active" @change="(e) => toggleNotification(user, e.target.checked)" class="user-checkbox" />
                    </label>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </div>
      </div>

      <div class="modal-footer">
        <button class="cancel-button" @click="handleClose">{{ $t('jobNotifications.buttons.cancel') }}</button>
        <button class="confirm-button" @click="handleSave">{{ $t('jobNotifications.buttons.save_changes') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { get, post } from '@/utils/api'
import { showToast } from '@/utils/toast'

export default {
  name: 'JobNotificationPopup',
  mounted() {
    this.fetchUsers()
    this.fetchNotificationConfig()
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    tenant: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      users: [],
      filteredUsers: [],
      notificationConfig: [],
      search: '',
      loading: false,
      selectedRole: '',
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.fetchUsers()
      }
    },
  },
  computed: {
    areAllFilteredUsersSelected() {
      const activeUsers = this.filteredUsers.filter((user) => user.is_active)
      return activeUsers.length > 0 && activeUsers.every((user) => user.notification_active)
    },
    getIsActive(userId) {
      const config = this.notificationConfig.find((config) => config.userId === userId)
      return config?.is_active ? 1 : 0
    },
  },
  methods: {
    async fetchNotificationConfig() {
      this.loading = true
      try {
        // First get all users

        // Then get email notification configs
        const configsUrl = `/v1/emailNotificationConfig/${this.tenant.id}`
        const configsResponse = await get(configsUrl)

        if (configsResponse?.data?.code === 200) {
          const configs = configsResponse.data.data || []
          this.notificationConfig = configs
        }
      } catch (error) {
        showToast({
          title: this.$t('jobNotifications.messages.error_title'),
          message: this.$t('jobNotifications.messages.error_fetch'),
          type: 'error',
        })
      } finally {
        this.loading = false
      }
    },
    async fetchUsers() {
      this.loading = true
      try {
        // First get all users
        const usersUrl = `/v1/users?page=1&pageSize=1000000&tenantId=${this.tenant.id}`
        const usersResponse = await get(usersUrl)

        // Then get email notification configs
        const configsUrl = `/v1/emailNotificationConfig/${this.tenant.id}`
        const configsResponse = await get(configsUrl)

        if (usersResponse?.data?.code === 200 && configsResponse?.data?.code === 200) {
          const users = usersResponse.data.data.users.filter((user) => user.status !== 'Invited')
          const configs = Array.isArray(configsResponse.data.data) ? configsResponse.data.data : []

          // Create a map of user IDs to their notification configs
          const configMap = new Map(configs.map((config) => [config.userId, config]))

          this.users = users.map((user) => {
            const config = configMap.get(user.id)
            return {
              id: user.id,
              name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email_id,
              email: user.email_id,
              is_active: user.is_active,
              notification_active: config ? config.is_active : false,
              status: user.status,
              user_roles: user.user_roles,
            }
          })

          this.filteredUsers = [...this.users]
        } else if (usersResponse?.data?.code === 200) {
          // If only users API succeeds but config API fails, still show users with default notification settings
          const users = usersResponse.data.data.users.filter((user) => user.status !== 'Invited')

          this.users = users.map((user) => ({
            id: user.id,
            name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email_id,
            email: user.email_id,
            is_active: user.is_active,
            notification_active: false, // Default to false when config API fails
            status: user.status,
            user_roles: user.user_roles,
          }))

          this.filteredUsers = [...this.users]
        }
      } catch (error) {
        showToast({
          title: this.$t('jobNotifications.messages.error_title'),
          message: this.$t('jobNotifications.messages.error_fetch'),
          type: 'error',
        })
      } finally {
        this.loading = false
      }
    },
    filterUsers() {
      let filtered = [...this.users]

      // Filter by role
      if (this.selectedRole) {
        filtered = filtered.filter((user) => user.user_roles?.some((role) => role.role.roleName === this.selectedRole))
      }

      // Filter by search text
      if (this.search) {
        const searchLower = this.search.toLowerCase()
        filtered = filtered.filter((user) => user.name.toLowerCase().includes(searchLower) || user.email.toLowerCase().includes(searchLower))
      }

      this.filteredUsers = filtered
    },
    toggleNotification(user, value) {
      user.notification_active = value
    },
    toggleSelectAll(event) {
      const isChecked = event.target.checked
      this.filteredUsers.forEach((user) => {
        user.notification_active = isChecked
      })
    },
    async handleSave() {
      try {
        // Get tenant_id from the tenant prop
        const tenant_id = this.tenant.id

        const updatedConfigs = this.users.map((user) => ({
          userId: user.id,
          tenant_id,
          is_active: user.notification_active,
          created_by: user.id,
          updated_by: user.id,
        }))
        const response = await post('/v1/emailNotificationConfig', updatedConfigs)

        if (response?.data?.code === 201) {
          showToast({
            title: this.$t('jobNotifications.messages.success_title'),
            message: this.$t('jobNotifications.messages.success_message'),
            type: 'success',
          })
          this.handleClose()
        }
      } catch (error) {
        showToast({
          title: this.$t('jobNotifications.messages.error_title'),
          message: this.$t('jobNotifications.messages.error_update'),
          type: 'error',
        })
      }
    },
    handleClose() {
      this.$emit('close')
    },
  },
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
}

.modal-content {
  width: 700px;
  max-height: 80vh;
  background: white;
  padding: 24px;
  border-radius: 10px;
  border: 1px solid #e8e8e8;
  box-shadow: 0px 4px 13.1px 0px rgba(0, 0, 0, 0.1);
  position: relative;
  display: flex;
  flex-direction: column;
}

.close-button {
  position: absolute;
  right: 14px;
  top: 14px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.modal-header {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 2px;
  gap: 2px;
}

.modal-header-icon {
  display: flex;
  width: 100px;
  height: 100px;
  padding: var(--Micro-XL, 12px);
  justify-content: center;
  align-items: center;
  border-radius: 50px;
  background: #f0f9fd;
}

.modal-header-title {
  color: var(--primary-color);
  text-align: center;
  font-family: Archivo;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 40px;
  margin-top: 5px;
}

.modal-header-subtitle {
  color: #5a5c62;
  text-align: center;
  font-family: Archivo;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  margin-top: -5px;
}

.modal-body {
  margin: 5px 0;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.filters-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.tenant-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
  padding: 8px 2px;
  background: #f8f9fa;
  border-radius: 6px;
}

.tenant-label {
  color: #5a5c62;
  font-family: Archivo;
  font-size: 14px;
  font-weight: 500;
}

.tenant-value {
  color: var(--primary-color);
  font-family: Archivo;
  font-size: 14px;
  font-weight: 600;
}

.filters-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.role-filter {
  flex: 1;
}

.role-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d1d1;
  border-radius: 6px;
  background: #fff;
  font-family: Archivo;
  font-size: 14px;
  color: #32393e;
  cursor: pointer;
  outline: none;
}

.role-select:focus {
  border-color: var(--primary-color);
}

.search-container {
  margin-bottom: 20px;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #d1d1d1;
  border-radius: 6px;
  background: #fff;
}

.search-input {
  border: none;
  outline: none;
  width: 100%;
  font-family: Archivo;
  font-size: 14px;
  color: #32393e;
}

.search-input::placeholder {
  color: #8f969b;
}

.users-list {
  max-height: calc(80vh - 250px);
  overflow-y: auto;
  margin-top: -30px;
  flex: 1;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
  font-family: Archivo;
}

.users-table th,
.users-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e8e8e8;
}

.users-table th {
  background: var(--primary-color);
  color: white;
  font-weight: 600;
  font-size: 14px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.srno-header {
  width: 50px;
}

.name-header {
  width: 40%;
}

.role-header {
  width: 35%;
}

.action-header {
  width: 170px;
  /* background-color: red !important; */
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
}

.user-row:hover {
  background-color: var(--secondary-color);
}

.user-srno {
  color: #8f969b;
  font-size: 14px;
}

.user-name {
  color: #32393e;
  font-size: 14px;
  font-weight: 500;
}

.user-role {
  color: #32393e;
  font-size: 14px;
  /* font-style: italic; */
}

.user-action {
  text-align: center;
}

.modal-footer {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e8e8e8;
  flex-wrap: wrap;
}

.cancel-button,
.confirm-button {
  min-width: 120px;
  height: 35px;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-family: Archivo;
  font-weight: 500;
  white-space: nowrap;
  flex: 0 0 auto;
}

.cancel-button {
  background: transparent;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
}

.confirm-button {
  background: var(--primary-color);
  border: none;
  color: white;
}

.select-all-container {
  padding: 0;
  border: none;
  display: flex;
  align-items: center;
}

.select-all-label {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 10px;
  cursor: pointer;
  font-family: Archivo;
  font-size: 14px;
  color: #32393e;
}

.select-all-label-text {
  /* padding-bottom: 10px; */
  /* margin-bottom: 20px; */
  /* margin-right: 5px; */
}

.select-all-checkbox,
.user-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: var(--primary-color);
  border: 1px solid #d1d1d1;
  border-radius: 3px;
  /* padding-top: 15px; */
}

.select-all-checkbox:checked,
.user-checkbox:checked {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.checkbox-label {
  display: flex;
  align-items: center;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  gap: 16px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: #5a5c62;
  font-family: Archivo;
  font-size: 14px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.no-results {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: #5a5c62;
  font-family: Archivo;
  font-size: 14px;
  text-align: center;
}
</style>
